import { Fragment, useState } from "react";
import { ChevronRight, ChevronDown, Pencil, Receipt, AlertTriangle, Quote } from "lucide-react";
import { money, pct } from "../../lib/api";
import BudgetRowDetail from "./BudgetRowDetail";

const FADE_ROW = { red: "bg-rose-50/70", amber: "bg-amber-50/60", none: "" };

const PROFIT_COLS = [
  ["base_contract", "Base contract", "money"],
  ["base_budget", "Base budget", "money"],
  ["base_profit", "Base profit", "money"],
  ["base_margin_pct", "Base margin", "pct"],
  ["co_revenue", "Processed Approved COs", "money"],
  ["co_cost", "CO budget", "money"],
  ["pending_revenue", "Submitted / Processed Unapproved COs", "money"],
  ["revised_contract", "Revised contract", "money"],
  ["revised_budget", "Revised budget", "money"],
  ["current_profit", "Current profit", "money"],
  ["current_margin_pct", "Current margin", "pct"],
  ["billed_to_date", "Billed to date", "money"],
  ["billed_pct", "% billed", "pct"],
];

const COST_COLS = [
  ["base_budget", "Base budget", "money"],
  ["co_cost", "CO budget changes", "money"],
  ["revised_budget", "Revised budget", "money"],
  ["committed", "Committed (POs)", "money"],
  ["direct_cost", "Direct costs", "money"],
  ["projected_cost", "Projected cost", "money"],
  ["forecast_to_complete", "Forecast to complete", "money"],
  ["eac", "Est at completion", "money"],
  ["gain_fade", "Gain / fade", "money"],
];

const TRACE = {
  base_contract: "base_contract", base_budget: "base_budget", base_profit: "base_profit",
  revised_contract: "revised_contract", revised_budget: "revised_budget",
  current_profit: "current_profit", pending_revenue: "pending_co", billed_to_date: "billed",
  projected_cost: "projected_cost", eac: "eac", forecast_to_complete: "ftc",
  gain_fade: "gain_fade", co_revenue: "revised_contract", co_cost: "revised_budget",
  base_margin_pct: "base_profit", current_margin_pct: "current_profit",
  billed_pct: "billed", committed: "projected_cost", direct_cost: "projected_cost",
};

const cell = (row, key, kind) => {
  const v = row[key];
  if (kind === "pct") return pct(v);
  return money(v);
};

/** The SOV-as-budget grid. Two views over the same rows, three groupings, every cell traceable. */
export default function BudgetTable({ project, data, view, onOverride, onDirectCost, onChanged }) {
  const [open, setOpen] = useState({});
  const [openRow, setOpenRow] = useState(null);
  const cols = view === "profit" ? PROFIT_COLS : COST_COLS;
  const groups = data.groups || [];

  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white"
         data-testid={`ops-budget-table-${view}`}>
      <table className="w-full min-w-[1100px] text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
          <tr>
            <th className="sticky left-0 z-10 bg-slate-50 px-4 py-2 text-left">
              {data.grouping === "scope" ? "Scope" : data.grouping === "cost_type" ? "Cost type" : "Cost code"}
            </th>
            {cols.map(([key, label]) => (
              <th key={key} className="whitespace-nowrap px-3 py-2 text-right">{label}</th>
            ))}
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groups.length === 0 && (
            <tr><td colSpan={cols.length + 2} className="px-4 py-10 text-center text-sm text-slate-500">
              No budget rows yet.
            </td></tr>
          )}
          {groups.map((g) => (
            <Fragment key={g.key}>
              <tr className={`cursor-pointer bg-slate-50/70 font-semibold text-slate-800 hover:bg-slate-100 ${FADE_ROW[g.fade]}`}
                  onClick={() => toggle(g.key)} data-testid={`ops-budget-group-${g.key}`}>
                <td className="sticky left-0 z-10 bg-inherit px-4 py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {open[g.key] ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                    <span className="font-mono text-xs">{g.key}</span>
                    <span className="text-xs font-normal text-slate-500">{g.label}</span>
                  </span>
                </td>
                {cols.map(([key, label, kind]) => (
                  <td key={key} title={`${label} — sum of ${g.rows.length} row(s) in ${g.key}`}
                      data-testid={`ops-budget-group-cell-${g.key}-${key}`}
                      className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                        key === "gain_fade" && g[key] < 0 ? "text-rose-700" : ""}`}>
                    {cell(g, key, kind)}
                  </td>
                ))}
                <td />
              </tr>
              {open[g.key] && g.rows.map((r) => (
                <Fragment key={r.id}>
                  <tr className={`${(r.forecast_log || []).length ? "bg-sky-50/50" : FADE_ROW[r.fade]} hover:bg-slate-50`} data-testid={`ops-budget-row-${r.row_key}`}>
                    <td className="sticky left-0 z-10 bg-inherit px-4 py-2 pl-9">
                      <button onClick={() => setOpenRow(openRow === r.id ? null : r.id)}
                              className="inline-flex items-center gap-1.5 text-left text-slate-700 hover:text-slate-950"
                              data-testid={`ops-budget-row-expand-${r.row_key}`}>
                        {openRow === r.id ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        <span className="font-mono text-xs">{r.cost_code || "—"}.{r.cost_type}</span>
                        <span className="text-xs text-slate-500">{r.cost_type_name}</span>
                      </button>
                      {r.orphan && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
                          <AlertTriangle className="h-2.5 w-2.5" /> off the estimate
                        </span>
                      )}
                      {r.eac_override !== null && r.eac_override !== undefined && (
                        <span className="ml-2 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] text-violet-700"
                              title={`${r.override_note} — ${r.override_by}`}>PM projection</span>
                      )}
                      {(r.forecast_log || []).length > 0 && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[10px] text-sky-700"
                              data-testid={`ops-budget-forecast-chip-${r.row_key}`}
                              title={r.forecast_log.slice(-5).map((e) => `${(e.at || "").slice(0, 10)} · ${e.by} · ${e.source === "quote" ? `quote ${e.quote_ref} ${e.delta > 0 ? "+" : ""}${money(e.delta)}` : `forecast set to ${e.value === null ? "—" : money(e.value)}`}\n${e.note || ""}`).join("\n\n")}>
                          <Quote className="h-2.5 w-2.5" /> forecast {r.forecast_adjustment ? `${r.forecast_adjustment > 0 ? "+" : ""}${money(r.forecast_adjustment)}` : "edited"}
                        </span>
                      )}
                    </td>
                    {cols.map(([key, , kind]) => (
                      <td key={key} title={r.trace?.[TRACE[key]] || ""}
                          data-testid={`ops-budget-cell-${r.row_key}-${key}`}
                          onClick={key === "forecast_to_complete" && data.can_override ? () => onOverride(r, "forecast_to_complete") : undefined}
                          className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                            key === "gain_fade" && r[key] < 0 ? "font-semibold text-rose-700" : ""} ${
                            key === "pending_revenue" && r[key] > 0 ? "text-amber-700" : ""} ${
                            key === "forecast_to_complete" && data.can_override ? "cursor-pointer underline decoration-dotted decoration-slate-300 underline-offset-4 hover:bg-slate-100" : ""} ${
                            key === "forecast_to_complete" && (r.forecast_log || []).length ? "bg-sky-50 font-semibold text-sky-800" : ""}`}>
                        {cell(r, key, kind)}
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-2 py-2 text-right">
                      {data.can_override && (
                        <span className="inline-flex gap-1">
                          <button onClick={() => onDirectCost(r)} title="Direct costs"
                                  data-testid={`ops-budget-direct-${r.row_key}`}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                            <Receipt className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => onOverride(r)} title="PM override"
                                  data-testid={`ops-budget-override-${r.row_key}`}
                                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                  {openRow === r.id && (
                    <tr data-testid={`ops-budget-bom-${r.row_key}`}>
                      <td colSpan={cols.length + 2} className="bg-slate-50/60 px-4 py-3">
                        <BudgetRowDetail project={project} row={r} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </tbody>
        <tfoot className="border-t-2 border-slate-200 bg-white font-semibold text-slate-900">
          <tr data-testid="ops-budget-totals">
            <td className="sticky left-0 z-10 bg-white px-4 py-2.5">Job total</td>
            {cols.map(([key, label, kind]) => (
              <td key={key} title={`${label} — job total across ${data.totals.rows} budget rows`}
                  className={`whitespace-nowrap px-3 py-2.5 text-right tabular-nums ${
                key === "gain_fade" && data.totals[key] < 0 ? "text-rose-700" : ""}`}
                  data-testid={`ops-budget-total-${key}`}>
                {cell(data.totals, key, kind)}
              </td>
            ))}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
